import { defaultTheme } from "./defaultTheme";

export function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex({ r, g, b }) {
  const c = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

export function mix(hexA, hexB, weight) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  return rgbToHex({
    r: a.r + (b.r - a.r) * weight,
    g: a.g + (b.g - a.g) * weight,
    b: a.b + (b.b - a.b) * weight,
  });
}

export const darken = (hex, amt) => mix(hex, "#000000", amt);
export const lighten = (hex, amt) => mix(hex, "#ffffff", amt);

export function alpha(hex, a) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function luminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const f = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export const onColor = (hex) => (luminance(hex) > 0.45 ? "#111827" : "#ffffff");

export function normalizeTheme(t) {
  if (!t) return defaultTheme;
  return {
    ...defaultTheme,
    ...t,
    colors: { ...defaultTheme.colors, ...(t.colors || {}) },
    fonts: { ...defaultTheme.fonts, ...(t.fonts || {}) },
    radius: typeof t.radius === "number" ? t.radius : defaultTheme.radius,
  };
}

// Tema nesnesinden CSS değişkenleri üretir. Türetilmiş tonlar burada hesaplanır,
// böylece admin sadece 8 ana rengi seçer.
export function buildCssVars(theme) {
  const t = normalizeTheme(theme);
  const c = t.colors;
  const isDark = luminance(c.bg) < 0.3;
  return {
    "--color-primary": c.primary,
    "--color-primary-hover": darken(c.primary, 0.12),
    "--color-primary-soft": isDark ? alpha(c.primary, 0.16) : mix(c.primary, c.surface, 0.9),
    "--color-primary-soft-2": isDark ? alpha(c.primary, 0.28) : mix(c.primary, c.surface, 0.78),
    "--color-on-primary": onColor(c.primary),
    "--color-primary-glow": alpha(c.primary, 0.45),
    "--color-accent": c.accent,
    "--color-accent-soft": isDark ? alpha(c.accent, 0.18) : mix(c.accent, c.surface, 0.86),
    "--color-on-accent": onColor(c.accent),
    "--color-accent-glow": alpha(c.accent, 0.25),
    "--color-dark": c.dark,
    "--color-dark-2": lighten(c.dark, 0.07),
    "--color-dark-3": lighten(c.dark, 0.14),
    "--color-on-dark": "#F8FAFC",
    "--color-on-dark-muted": alpha("#F8FAFC", 0.68),
    "--color-bg": c.bg,
    "--color-surface": c.surface,
    "--color-surface-2": isDark ? lighten(c.surface, 0.05) : mix(c.surface, c.bg, 0.6),
    "--color-text": c.text,
    "--color-muted": c.muted,
    "--color-border": c.border,
    "--color-border-strong": isDark ? lighten(c.border, 0.12) : darken(c.border, 0.12),
    "--font-heading": `'${t.fonts.heading}', 'Segoe UI', system-ui, sans-serif`,
    "--font-body": `'${t.fonts.body}', 'Segoe UI', system-ui, sans-serif`,
    "--radius": `${t.radius}px`,
    "--radius-sm": `${Math.max(4, Math.round(t.radius * 0.5))}px`,
    "--radius-lg": `${Math.round(t.radius * 1.6)}px`,
    "--shadow-sm": isDark ? "0 1px 2px rgba(0,0,0,.5)" : "0 1px 2px rgba(16,24,40,.06)",
    "--shadow-md": isDark ? "0 8px 24px rgba(0,0,0,.5)" : "0 8px 24px rgba(16,24,40,.08)",
    "--shadow-lg": isDark ? "0 24px 48px rgba(0,0,0,.6)" : "0 24px 48px -12px rgba(16,24,40,.18)",
    "--scheme": isDark ? "dark" : "light",
  };
}

export function applyTheme(theme, root = document.documentElement) {
  const vars = buildCssVars(theme);
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  const t = normalizeTheme(theme);
  root.dataset.header = t.headerStyle;
  root.dataset.hero = t.heroStyle;
  root.style.colorScheme = vars["--scheme"];
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", t.colors.dark);
  ensureFonts(t.fonts);
}

const loadedFonts = new Set();
export function ensureFonts(fonts) {
  const families = [fonts.heading, fonts.body].filter(Boolean);
  const missing = families.filter((f) => !loadedFonts.has(f));
  if (!missing.length) return;
  missing.forEach((f) => loadedFonts.add(f));
  const fam = missing
    .map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;500;600;700;800`)
    .join("&");
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${fam}&display=swap`;
  document.head.appendChild(link);
}

// index.html'de önceden yüklü fontlar; tekrar yüklenmesin.
["Manrope", "Inter"].forEach((f) => loadedFonts.add(f));
